import * as css from 'css/exports.module.scss';

import './CreateTask.scss';

import { Key } from 'ts-key-enum';
import { useCreateInitiativeTask } from '@hooks';
import { When } from 'react-if';

import { Button, Input, Space } from 'antd';
import {
	COPY_GENERIC,
	COPY_SPECIFIC,
	INITIATIVES_NEW_TASK,
	PLACEHOLDERS_COPY
} from '@sonar-software/sonar-ts-constants';
import Form, { FormInstance, Rule } from 'antd/es/form';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface CreateTaskProps {
	initiativeId: number;
}

const newTaskRules: Rule[] = [
	{
		max: INITIATIVES_NEW_TASK.MAX_LENGTH,
		message: INITIATIVES_NEW_TASK.MAX_MESSAGE,
		type: 'string'
	}
];

export const CreateTask: React.FC<CreateTaskProps> = ({ initiativeId }) => {
	const [ addEnable, setAddEnabled ] = useState(false);

	const { isProcessing, isSuccessful, request } = useCreateInitiativeTask(initiativeId);

	const formRef = useRef<FormInstance>(null);

	const resetForm = useCallback(() => {
		formRef.current?.resetFields();
	}, []);

	useEffect(() => {
		if (isSuccessful) {
			resetForm();
		}
	}, [ isSuccessful, resetForm ]);

	const handleCancelClick = useCallback(() => {
		resetForm();
		setAddEnabled(false);
	}, [ resetForm, setAddEnabled ]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === Key.Escape) {
				handleCancelClick();
			}
		},
		[ handleCancelClick ]
	);

	const onAdd = useCallback(() => {
		const name = formRef.current?.getFieldValue('newTaskName');
		const taskName = (name ?? '').trim();

		if (!taskName) {
			return;
		}

		request({ name });
	}, [ request ]);

	return (
		<>
			<When condition={ !addEnable }>
				<Button
					className='add-task-input'
					id='initiative-add-task-button'
					onClick={ () => setAddEnabled(true) }
					type='default'>
					{ COPY_SPECIFIC.CREATE_NEW_TASK }
				</Button>
			</When>
			<When condition={ addEnable }>
				<Form
					onFinish={ onAdd }
					ref={ formRef }>
					<Form.Item
						name='newTaskName'
						rules={ newTaskRules }>
						<Input
							allowClear
							autoFocus={ addEnable }
							className='add-task-input'
							disabled={ isProcessing }
							id='initiative-add-task-input'
							onKeyDown={ handleKeyDown }
							placeholder={ PLACEHOLDERS_COPY.ADD_A_TASK }
							type='text' />
					</Form.Item>
					<Space size={ Number(css.size4) }>
						<Button
							id='initiative-add-task-cancel-button'
							loading={ isProcessing }
							onClick={ handleCancelClick }>
							{ COPY_GENERIC.CANCEL }
						</Button>
						<Button
							htmlType='submit'
							id='initiative-add-task-confirm-button'
							loading={ isProcessing }
							type='primary'>
							{ COPY_SPECIFIC.SAVE_NEW_TASK }
						</Button>
					</Space>
				</Form>
			</When>
		</>
	);
};
